/* eslint-disable sort-keys */
/* eslint-disable max-lines */
const shared = {
    alignItems: 'center' as const,
    backgroundColor: '$buttonBackground',
    borderColor: '$buttonBorder',
    borderRadius: '$button',
    borderWidth: '$button',
    boxSizing: 'border-box',
    flexDirection: 'row' as const,
    justifyContent: 'center' as const,
    padding: '$buttonPaddingVertical $buttonPaddingHorizontal',
    variants: {
        color: {
            filled: {
                backgroundColor: '$buttonFilledBackground',
                borderColor: '$buttonFilledBorder',
            },
            text: {
                backgroundColor: '$buttonTextBackground',
                borderColor: '$buttonTextBorder',
            },
            highlight: {
                backgroundColor: 'transparent',
                borderColor: 'transparent',
            },
        },
        size: {
            large: {
                paddingBottom: '$buttonPaddingVerticalLarge',
                paddingLeft: '$buttonPaddingHorizontalLarge',
                paddingRight: '$buttonPaddingHorizontalLarge',
                paddingTop: '$buttonPaddingVerticalLarge',
            },
            small: {
                paddingBottom: '$buttonPaddingVerticalSmall',
                paddingLeft: '$buttonPaddingHorizontalSmall',
                paddingRight: '$buttonPaddingHorizontalSmall',
                paddingTop: '$buttonPaddingVerticalSmall',
            },
            unstyled: {
                flexGrow: 1,
                justifyContent: 'flex-start' as const,
                padding: '2px',
                textAlign: 'left' as const,
            },
        },
    },
}

export const button = {
    ...shared,
    'fontSize': '$button',
    'fontWeight': '$button',
    'borderStyle': '$button',
    'fontFamily': '$button',
    'lineHeight': '$button',
    'fontStyle': '$button',
    'fontVariant': '$button',
    '& svg': {
        color: '$buttonIcon',
        flex: 'none',
        height: '$buttonIcon',
        width: '$buttonIcon',
    },
    '&:disabled': {
        '& svg': {color: '$buttonIconDisabled'},
        'cursor': 'not-allowed',
        'backgroundColor': '$buttonBackgroundDisabled',
        'borderColor': '$buttonBorderDisabled',
    },
    '&:not(:disabled)': {
        '&:active, &:hover:active': {
            '& svg': {color: '$buttonIconActive'},
            '[data-id="button-text"]': {color: '$buttonTextActive'},
            'backgroundColor': '$buttonBackgroundActive',
            'borderColor': '$buttonBorderActive',
        },
        '&:focus-visible': {
            '& svg': {color: '$buttonIconFocus'},
            '[data-id="button-text"]': {color: '$buttonTextFocus'},
            'backgroundColor': '$buttonBackgroundFocus',
            'borderColor': '$buttonBorderFocus',
            'outlineColor': '$buttonOutlineFocus',
            'outlineOffset': '1px',
            'outlineStyle': 'solid',
            'outlineWidth': '2px',
        },
        '&:hover': {
            '& svg': {color: '$buttonIconHover'},
            '[data-id="button-text"]': {color: '$buttonTextHover'},
            'backgroundColor': '$buttonBackgroundHover',
            'borderColor': '$buttonBorderHover',
        },
    },
    'display': 'flex',
    'overflow': 'hidden',
    'variants': {
        ...shared.variants,
        color: {
            ...shared.variants.color,
            filled: {
                ...shared.variants.color.filled,
                'borderRadius': '$buttonFilled',
                'borderWidth': '$buttonFilled',
                'borderStyle': '$buttonFilled',
                'fontSize': '$buttonFilled',
                'fontStyle': '$buttonFilled',
                'fontVariant': '$buttonFilled',
                'fontWeight': '$buttonFilled',
                'fontFamily': '$buttonFilled',
                'lineHeight': '$buttonFilled',
                '& svg': {color: '$buttonFilledIcon'},
                '&:disabled': {
                    '& svg': {color: '$buttonFilledIconDisabled'},
                    'backgroundColor': '$buttonFilledBackgroundDisabled',
                    'borderColor': '$buttonFilledBorderDisabled',
                },
                '&:not(:disabled)': {
                    '&:active, &:hover:active': {
                        '& svg': {color: '$buttonFilledIconActive'},
                        '[data-id="button-text"]': {color: '$buttonFilledTextActive'},
                        'backgroundColor': '$buttonFilledBackgroundActive',
                        'borderColor': '$buttonFilledBorderActive',
                    },
                    '&:focus-visible': {
                        '& svg': {color: '$buttonFilledIconFocus'},
                        '[data-id="button-text"]': {color: '$buttonFilledTextFocus'},
                        'backgroundColor': '$buttonFilledBackgroundFocus',
                        'borderColor': '$buttonFilledBorderFocus',
                        'outlineColor': '$buttonFilledOutlineFocus',
                    },
                    '&:hover': {
                        '& svg': {color: '$buttonFilledIconHover'},
                        '[data-id="button-text"]': {color: '$buttonFilledTextHover'},
                        'backgroundColor': '$buttonFilledBackgroundHover',
                        'borderColor': '$buttonFilledBorderHover',
                    },
                },
            },
            text: {
                ...shared.variants.color.text,
                'borderRadius': '$buttonText',
                'borderWidth': '$buttonText',
                'borderStyle': '$buttonText',
                'fontSize': '$buttonText',
                'fontWeight': '$buttonText',
                'fontFamily': '$buttonText',
                'lineHeight': '$buttonText',
                'fontStyle': '$buttonText',
                'fontVariant': '$buttonText',
                '& svg': {color: '$buttonTextIcon'},

                '&:disabled': {
                    '& svg': {color: '$buttonTextIconDisabled'},
                    'backgroundColor': '$buttonTextBackgroundDisabled',
                    'borderColor': '$buttonTextBorderDisabled',
                },
                '&:not(:disabled)': {
                    '&:active, &:hover:active': {
                        '& svg': {color: '$buttonTextIconActive'},
                        '[data-id="button-text"]': {color: '$buttonTextContentTextActive'},
                        'backgroundColor': '$buttonTextBackgroundActive',
                        'borderColor': '$buttonTextBorderActive',
                    },
                    '&:focus-visible': {
                        '& svg': {color: '$buttonTextIconFocus'},
                        '[data-id="button-text"]': {color: '$buttonTextContentTextFocus'},
                        'backgroundColor': '$buttonTextBackgroundFocus',
                        'borderColor': '$buttonTextBorderFocus',
                        'outlineColor': '$buttonTextOutlineFocus',
                    },
                    '&:hover': {
                        '& svg': {color: '$buttonTextIconHover'},
                        '[data-id="button-text"]': {color: '$buttonTextContentTextHover'},
                        'backgroundColor': '$buttonTextBackgroundHover',
                        'borderColor': '$buttonTextBorderHover',
                    },
                },
            },
            highlight: {
                ...shared.variants.color.highlight,
                '& svg': {color: '$highlight'},
                '&:not(:disabled)': {
                    '&:focus-visible': {backgroundColor: '$buttonTextBackgroundFocus'},
                    '&:hover': {
                        backgroundColor: '$buttonTextBackgroundHover',
                        borderColor: 'transparent',
                    },
                },
            },
        },
        overflowVisible: {true: {overflow: 'visible'}},
        size: {
            ...shared.variants.size,
            small: {
                ...shared.variants.size.small,
                '& svg': {
                    height: '$buttonIconSmall',
                    width: '$buttonIconSmall',
                },
            },
        },
        spacing: {left: {marginLeft: '10px'}},
    },
}

export const buttonText = {
    '&:not(:first-child)': {marginLeft: '$buttonTextMargin'},
    '&:not(:last-child)': {marginRight: '$buttonTextMargin'},
    'color': '$buttonText',
    'overflow': 'hidden',
    'textOverflow': 'ellipsis',
    'variants': {
        color: {
            filled: {color: '$buttonFilledText'},
            highlight: {},
            text: {color: '$buttonTextContentText'},
        },
        disabled: {true: {color: '$buttonTextDisabled'}},
        overflowVisible: {true: {overflow: 'visible'}},
        multiline: {true: {whiteSpace: 'normal'}},
    },
    // eslint-disable-next-line sort-keys
    'compoundVariants': [{
        color: 'filled',
        css: {color: '$buttonFilledTextDisabled'},
        disabled: true,
    },
    {
        color: 'text',
        css: {color: '$buttonTextContentTextDisabled'},
        disabled: true,
    }],
    'whiteSpace': 'nowrap',
}

export const nativeButtonContainer = {
    alignSelf: 'flex-start' as const,
    variants: {spacing: {left: {marginLeft: 10}}},
}

export const nativeButton = {
    ...shared,
    variants: {
        ...shared.variants,
        margin: {left: {marginLeft: 10}},
        state: {
            active: {
                backgroundColor: '$buttonBackgroundActive',
                borderColor: '$buttonBorderActive',
            },
            filledActive: {
                backgroundColor: '$buttonFilledBackgroundActive',
                borderColor: '$buttonFilledBorderActive',
            },
            filledDisabled: {
                backgroundColor: '$buttonFilledBackgroundDisabled',
                borderColor: '$buttonFilledBorderDisabled',
            },
        },
    },
}

export const nativeButtonText = {
    color: '$buttonText',
    fontSize: '$buttonText',
    fontWeight: '$buttonText' as const,
    variants: {
        color: {
            disabled: {color: '$buttonTextDisabled'},
            filled: {color: '$buttonFilledText'},
            highlight: {},
            text: {},
        },
        withLeftIcon: {true: {marginLeft: '$buttonTextMargin'}},
        withRightIcon: {true: {marginRight: '$buttonTextMargin'}},
    },
}
